import React, { useState } from 'react'
import SbEditble from 'storyblok-react'
import { Form, Field } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { useAccordion } from 'hooks'
import { getIntlMessage } from 'utils/helpers'
import curry from 'lodash/curry'

import { Section, CButton, StyledMarkdown } from 'components/Common'
import { Container, Row, Col } from 'components/Grid'
import { TextField, SelectField, RadioField } from 'components/FormElements'
import { isRequired, isValidEmail, isValidPhoneNumber } from 'lib/form-validators'

import { Legend, Fieldset, LegendAccordion, Label } from './styles'
const SubmitButtonCSS = 'margin: 72px auto 162px; font-size: 16px;'

const composeFormValidator = (validator) => (parser) => (id) => validator(id, parser)

const requiredField = composeFormValidator(isRequired)
const validateEmail = composeFormValidator(isValidEmail)
const validatePhoneNr = composeFormValidator(isValidPhoneNumber)
const validatePostalCode = requiredField // temporarily use requiredField instead of postal code validator

function EventForm({ blok }) {
  const intl = useIntl()
  const intlParse = (curry)(id => getIntlMessage(intl.messages, id))

  function handleSubmit(e) {
    // noop
  }
  return (
    <SbEditble content={blok}>
      <Section>
        <Container>
          <Row>
            <Col>
              <Form
                onSubmit={handleSubmit}
                render={({ handleSubmit, pristine, invalid }) => (
                  <form acceptCharset="UTF-8" method="post" action={blok.action_url}>
                    {/* HIDDEN FIELDS */}
                    <input type="hidden" name="pardot_token" value={blok.pardot_token} />
                    <input type="hidden" name="pardot_adidnum" id="pardot_adidnum" value={blok.pardot_adidnum} />
                    <input
                      type="hidden"
                      name="pardot_event_id"
                      id="pardot_event_id"
                      value={blok.pardot_event_id}
                    />
                    <input
                      type="hidden"
                      name="pardot_google_keyword"
                      id="pardot_google_keyword"
                      value={blok.pardot_google_keyword}
                    />
                    <input type="hidden" name="pardot_event_id" id="pardot_event_id" value="" />
                    <input
                      type="hidden"
                      name="pardot_PIE_registration"
                      id="pardot_PIE_registration"
                      value={blok.pardot_PIE_registration}
                    />

                    <PersonalInfo intl={intlParse} />
                    <GuestSet intl={intlParse} />
                    <ChildSets sets={4} intl={intlParse} />
                    <DataConsent intl={intlParse} content={blok.data_consent} />

                    <CButton
                      htmlType="submit"
                      type="secondary"
                      size="big"
                      withArrow
                      disabled={pristine || invalid}
                      css={SubmitButtonCSS}
                    >
                      Register
                    </CButton>
                  </form>
                )}
              />
            </Col>
          </Row>
        </Container>
      </Section>
    </SbEditble>
  )
}

export default EventForm

const PersonalInfo = function({ intl }) {
  return (
    <Fieldset>
      <Legend>
        <FormattedMessage id="Guest Information" />
      </Legend>
      <p className="required">
        <Label htmlFor="pardot_first_name">
          <FormattedMessage id="First Name" />
        </Label>
        <Field
          name="pardot_first_name"
          size="30"
          maxLength="40"
          id="pardot_first_name"
          component={TextField}
          validate={requiredField(intl)}
        />
      </p>
      <p className="required">
        <Label htmlFor="pardot_last_name">
          <FormattedMessage id="Last Name" />
        </Label>
        <Field
          name="pardot_last_name"
          size="30"
          maxLength="80"
          id="pardot_last_name"
          component={TextField}
          validate={requiredField(intl)}
        />
      </p>
      <p className="required">
        <Label htmlFor="pardot_email">
          <FormattedMessage id="Email" />
        </Label>
        <Field
          name="pardot_email"
          size="30"
          maxLength="255"
          id="pardot_email"
          component={TextField}
          validate={validateEmail(intl)}
        />
      </p>
      <p className="required">
        <Label htmlFor="pardot_mobile">
          <FormattedMessage id="Mobile" />
        </Label>
        <Field
          name="pardot_mobile"
          size="30"
          maxLength="40"
          id="pardot_mobile"
          component={TextField}
          validate={validatePhoneNr(intl)}
        />
      </p>
      <p className="required">
        <Label htmlFor="pardot_zipcode">
          <FormattedMessage id="Zip/Post Code" />
        </Label>
        <Field
          name="pardot_zipcode"
          size="30"
          maxLength="32"
          id="pardot_zipcode"
          component={TextField}
          validate={validatePostalCode(intl)}
        />
      </p>
      <p className="required">
        <Label htmlFor="pardot_passport">
          <FormattedMessage id="RG/RNE/Passport ID" />
        </Label>
        <Field
          name="pardot_passport"
          size="30"
          maxLength="32"
          id="pardot_passport"
          component={TextField}
          validate={requiredField(intl)}
        />
      </p>
      <p>
        <Label htmlFor="pardot_how_select">
          <FormattedMessage id="How did you hear about Avenues?" />
        </Label>
        <Field
          name="pardot_how_select"
          id="pardot_how_select"
          validate={requiredField(intl)}
          type="select"
          render={props => (
            <SelectField
              options={[
                intl('Avenues employee'),
                intl('Friend or family'),
                intl('Online Advertising'),
                intl('Print Advertising'),
                intl('Internet Search'),
                intl('News Article'),
                intl('Relocation/Educational consultant'),
                intl('Other'),
              ]}
              placeholder={intl('Select')}
              required={requiredField(intl)}
              {...props}
            />
          )}
        />
      </p>
    </Fieldset>
  )
}

const GuestSet = function({ intl }) {
  const [guestsAmount, setGuestsAmount] = useState(0)

  const Guest = <FormattedMessage id="Guest" />

  function renderGuestsInputs() {
    const guestFields = []
    for (let i = 1; i <= guestsAmount; i++) {
      guestFields.push(
        <Fieldset style={{ marginBottom: '15px' }}>
          <p className="required">
            <Label htmlFor={`pardot_attendee${i}_first_name`}>
              {Guest} {i} <FormattedMessage id="First Name" />
            </Label>
            <Field
              name={`pardot_attendee${i}_first_name`}
              size="30"
              maxLength="65535"
              id={`pardot_attendee${i}_first_name`}
              component={TextField}
              validate={requiredField(intl)}
            />
          </p>
          <p className="required">
            <Label htmlFor="pardot_attendee1_last_name">
              {Guest} {i} <FormattedMessage id="Last Name" />
            </Label>
            <Field
              name={`pardot_attendee${i}_last_name`}
              size="30"
              maxLength="65535"
              id={`pardot_attendee${i}_last_name`}
              component={TextField}
              validate={requiredField(intl)}
            />
          </p>
          <p>
            <Label htmlFor="pardot_attendee1_email">
              {Guest} {i} <FormattedMessage id="Email" />
            </Label>
            <Field
              name={`pardot_attendee${i}_email`}
              size="30"
              maxLength="65535"
              id={`pardot_attendee${i}_email`}
              component={TextField}
              validate={validateEmail(intl)}
            />
          </p>
          <p>
            <Label htmlFor="pardot_attendee1_passport">
              {Guest} {i} <FormattedMessage id="RG/RNE/Passport ID" />
            </Label>
            <Field
              name={`pardot_attendee${i}_passport`}
              size="30"
              maxLength="65535"
              id={`pardot_attendee${i}_passport`}
              component={TextField}
              validate={requiredField(intl)}
            />
          </p>
        </Fieldset>,
      )
    }
    return guestFields
  }

  return (
    <Fieldset>
      <Legend>
        <FormattedMessage id="Guests" />
      </Legend>
      <p className="required">
        <Label htmlFor="pardot_people_party">
          <FormattedMessage id="Total Number of Additional Attendees" />
        </Label>
        <Field
          name="pardot_people_party"
          id="pardot_people_party"
          validate={requiredField(intl)}
          type="select"
          render={props => (
            <SelectField
              onChange={setGuestsAmount}
              options={[
                { value: 0, label: intl('Just me, no additional attendees') },
                { value: 1, label: intl('+1') },
                { value: 2, label: intl('2+') },
              ]}
              placeholder={intl('Select')}
              required={requiredField(intl)}
              {...props}
            />
          )}
        />
      </p>

      {/* GUESTS INFO INPUTS */}
      {guestsAmount > 0 && renderGuestsInputs()}
    </Fieldset>
  )
}

const ChildSets = function(props) {
  function renderChildFields() {
    const childFields = []
    for (let i = 1; i <= props.sets; i++) {
      childFields.push(<ChildSet label={i} isRequired={i === 1} intl={props.intl} />)
    }
    return childFields
  }
  return <>{renderChildFields()}</>
}

const ChildSet = function(props) {
  const [ref, toggleAccordion, height] = useAccordion()
  const { intl } = props

  return (
    <Fieldset>
      <LegendAccordion onClick={toggleAccordion} isOpen={!!height}>
        <FormattedMessage id="Child" /> {props.label} <FormattedMessage id="Information" />
      </LegendAccordion>
      <div
        ref={ref}
        height={height}
        style={{ height, overflowY: 'hidden', transition: 'height .3s', paddingLeft: '5px' }}
      >
        <p className="required">
          <Label htmlFor={`pardot_child${props.label}_first_name`}>
            <FormattedMessage id="First Name" />
          </Label>
          <Field
            name={`pardot_child${props.label}_first_name`}
            id={`pardot_child${props.label}_first_name`}
            component={TextField}
            validate={props.isRequired && requiredField(intl)}
            size="30"
            maxLength="65535"
          />
        </p>
        <p className="required">
          <Label htmlFor={`pardot_child${props.label}_last_name`}>
            <FormattedMessage id="Last Name" />
          </Label>
          <Field
            name={`pardot_child${props.label}_last_name`}
            id={`pardot_child${props.label}_last_name`}
            component={TextField}
            validate={props.isRequired && requiredField(intl)}
            size="30"
            maxLength="65535"
          />
        </p>
        <p>
          <Label htmlFor={`pardot_child${props.label}_gender`}>
            <FormattedMessage id="Gender" />
          </Label>
          <RadioField
            name={`pardot_child${props.label}_gender`}
            validate={props.isRequired && requiredField(intl)}
            options={[
              { value: 'M', label: intl('Male') },
              { value: 'F', label: intl('Female') },
              { value: '0', label: intl('Prefer not to say') },
            ]}
          />
        </p>
        <p className="required">
          <Label htmlFor={`pardot_child${props.label}_dob`}>
            <FormattedMessage id="Date of Birth" />
          </Label>
          <Field
            name={`pardot_child${props.label}_dob`}
            id={`pardot_child${props.label}_dob`}
            component={TextField}
            validate={props.isRequired && requiredField(intl)}
            size="12"
            placeholder="yyyy-mm-dd"
            maxLength="10"
          />
        </p>
        <p className="required">
          <Label htmlFor={`pardot_child${props.label}_entry_date`}>
            <FormattedMessage id="Entry Date" />
          </Label>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div style={{ width: '49.5%' }}>
              <Field
                name={`pardot_child${props.label}_entry_date_month`}
                id={`pardot_child${props.label}_entry_date_month`}
                validate={props.isRequired && requiredField(intl)}
                type="select"
                render={props => (
                  <SelectField
                    options={[
                      { value: 'January', label: intl('January') },
                      { value: 'February', label: intl('February') },
                      { value: 'March', label: intl('March') },
                      { value: 'April', label: intl('April') },
                      { value: 'May', label: intl('May') },
                      { value: 'June', label: intl('June') },
                      { value: 'July', label: intl('July') },
                      { value: 'August', label: intl('August') },
                      { value: 'September', label: intl('September') },
                      { value: 'October', label: intl('October') },
                      { value: 'November', label: intl('November') },
                      { value: 'December', label: intl('December') },
                    ]}
                    placeholder={intl('Select')}
                    required={props.isRequired && requiredField(intl)}
                    {...props}
                  />
                )}
              />
            </div>
            <div style={{ width: '49.5%' }}>
              <Field
                name={`pardot_child${props.label}_entry_date_year`}
                id={`pardot_child${props.label}_entry_date_year`}
                validate={props.isRequired && requiredField(intl)}
                type="select"
                render={props => (
                  <SelectField
                    options={['2019', '2020', '2021', '2022']}
                    placeholder={intl('Select')}
                    required={props.isRequired && requiredField(intl)}
                    {...props}
                  />
                )}
              />
            </div>
          </div>
        </p>

        <p className="required">
          <Label htmlFor={`pardot_child${props.label}_current_school`}>
            <FormattedMessage id="Current school" />
          </Label>
          <Field
            name={`pardot_child${props.label}_current_school`}
            id={`pardot_child${props.label}_current_school`}
            component={TextField}
            validate={props.isRequired && requiredField(intl)}
            size="30"
            maxLength="65535"
          />
        </p>
      </div>
    </Fieldset>
  )
}

const DataConsent = function({ content, intl }) {
  return (
    <Fieldset>
      <Legend>
        <FormattedMessage id="Data Consent" />
      </Legend>
      <Label css="div, p { display: inline; } cursor: pointer;">
        <Field
          type="checkbox"
          name="pardot_GDPR"
          id="pardot_GDPR"
          component="input"
          validate={requiredField(intl)}
        />{' '}
        <StyledMarkdown source={content} />
      </Label>
    </Fieldset>
  )
}
